import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import Http from '../../../../core/Http';
import {
  isValidPortalPassword,
  PASSWORD_POLICY_MESSAGE,
  PASSWORD_POLICY_PLACEHOLDER,
} from '../../../../assets/utils/comm/passwordPolicy';

/**
 * 로그인 화면용 비밀번호 재설정 공통 팝업 (OTP → 새 비밀번호)
 * config.initialData: { initialUserId?: string }
 * 닫기: onClose(false) | 성공 시 onClose(true, { passwordChanged: true })
 */
const ForgotPasswordPopup = ({ config, onClose }) => {
  const { t } = useTranslation();

  const [step, setStep] = useState(1);
  const [forgotUserId, setForgotUserId] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotPw1, setForgotPw1] = useState('');
  const [forgotPw2, setForgotPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resendHint, setResendHint] = useState('');

  // 팝업이 닫혔다가 다시 열릴 때마다 컴포넌트가 새로 마운트되므로, 여기서 한 번만 초기화한다.
  useEffect(() => {
    setStep(1);
    setForgotUserId(config?.initialData?.initialUserId ?? '');
    setForgotOtp('');
    setForgotPw1('');
    setForgotPw2('');
    setBusy(false);
    setError('');
    setResendHint('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 열릴 때마다 새 인스턴스 기준 초기값만 반영
  }, []);

  const apiMessage = (err) =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    t('login.errorLogin');

  const handleCancel = () => onClose(false);

  const handleSendOtp = async () => {
    const id = forgotUserId.trim();
    if (!id) {
      setError(t('login.errorRequired'));
      return;
    }
    setBusy(true);
    setError('');
    setResendHint('');
    try {
      await Http.post('/api/glow/otp', { userEmailId: id });
      setStep(2);
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    const id = forgotUserId.trim();
    if (!id) return;
    setBusy(true);
    setError('');
    setResendHint('');
    try {
      await Http.post('/api/glow/otp/resend', { userEmailId: id });
      setResendHint(t('login.forgotResendOk'));
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCheckOtp = async () => {
    const id = forgotUserId.trim();
    const code = forgotOtp.trim();
    if (!/^\d{4}$/.test(code)) {
      setError(t('login.forgotOtpInvalid'));
      return;
    }
    setBusy(true);
    setError('');
    setResendHint('');
    try {
      const data = await Http.post('/api/glow/otp/check', {
        userEmailId: id,
        otpNum: parseInt(code, 10),
      });
      if (data?.isSame) {
        setStep(3);
      } else {
        setError(data?.message || t('login.forgotOtpInvalid'));
      }
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleChangePassword = async () => {
    const id = forgotUserId.trim();
    const code = forgotOtp.trim();
    if (forgotPw1 !== forgotPw2) {
      setError(t('login.forgotPasswordMismatch'));
      return;
    }
    if (!isValidPortalPassword(forgotPw1)) {
      setError(PASSWORD_POLICY_MESSAGE);
      return;
    }
    if (!/^\d{4}$/.test(code)) {
      setError(t('login.forgotOtpInvalid'));
      setStep(2);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await Http.post('/api/glow/user/change-password', {
        userEmailId: id,
        password: forgotPw1,
        otpNum: parseInt(code, 10),
      });
      onClose(true, { passwordChanged: true });
    } catch (err) {
      const body = err?.response?.data;
      const ve = body?.errors;
      if (ve && typeof ve === 'object') {
        const first = Object.values(ve)[0];
        setError(typeof first === 'string' ? first : apiMessage(err));
      } else {
        setError(apiMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const title = config?.title || t('login.forgotTitle');

  return (
    <PopupWrapper
      config={{
        ...config,
        title,
        width: config?.width ?? 440,
      }}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-forgot-password"
    >
      <div className="main-popup-body">
        {step === 1 && (
          <>
            <p className="small text-muted mb-3">{t('login.forgotStep1Hint')}</p>
            <label className="form-label small mb-1" htmlFor="forgot-login-id">
              {t('login.userId')}
            </label>
            <input
              id="forgot-login-id"
              type="text"
              className="form-control mb-2"
              autoComplete="username"
              placeholder={t('login.userIdPlaceholder')}
              value={forgotUserId}
              onChange={(e) => setForgotUserId(e.target.value)}
              disabled={busy}
            />
          </>
        )}
        {step === 2 && (
          <>
            <p className="small text-muted mb-2">{t('login.forgotOtpLabel')}</p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              className="form-control mb-2"
              style={{ letterSpacing: '0.25em', fontSize: '1.1rem' }}
              placeholder="0000"
              value={forgotOtp}
              onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
              disabled={busy}
            />
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mb-2"
              onClick={handleResend}
              disabled={busy}
            >
              {t('login.forgotResend')}
            </button>
            {resendHint ? (
              <p className="small text-success mb-0">{resendHint}</p>
            ) : null}
          </>
        )}
        {step === 3 && (
          <>
            <label className="form-label small mb-1" htmlFor="forgot-pw1">
              {t('login.forgotNewPassword')}
            </label>
            <input
              id="forgot-pw1"
              type="password"
              className="form-control mb-2"
              placeholder={PASSWORD_POLICY_PLACEHOLDER}
              autoComplete="new-password"
              value={forgotPw1}
              onChange={(e) => setForgotPw1(e.target.value)}
              disabled={busy}
            />
            <label className="form-label small mb-1" htmlFor="forgot-pw2">
              {t('login.forgotConfirmPassword')}
            </label>
            <input
              id="forgot-pw2"
              type="password"
              className="form-control mb-0"
              placeholder={t('login.forgotConfirmPasswordPlaceholder')}
              autoComplete="new-password"
              value={forgotPw2}
              onChange={(e) => setForgotPw2(e.target.value)}
              disabled={busy}
            />
          </>
        )}
        {error ? <p className="small text-danger mt-3 mb-0">{error}</p> : null}
      </div>
      <div className="main-popup-footer forgot-password-popup-footer d-flex align-items-center flex-nowrap gap-2">
        <div className="d-flex gap-2 align-items-center forgot-password-footer-left">
          <button
            type="button"
            className={`btn forgot-password-footer-cancel ${getButtonClass('btn-secondary')}`}
            onClick={handleCancel}
            disabled={busy}
          >
            {t('login.forgotCancel')}
          </button>
          {step > 1 ? (
            <button
              type="button"
              className={`btn forgot-password-footer-back ${getButtonClass('btn-secondary')}`}
              onClick={() => {
                setError('');
                setResendHint('');
                setStep((s) => Math.max(1, s - 1));
              }}
              disabled={busy}
            >
              {t('login.forgotBack')}
            </button>
          ) : null}
        </div>
        <div className="d-flex gap-2 align-items-center forgot-password-footer-right">
          {step === 1 ? (
            <button
              type="button"
              className={`btn main-popup-right-btn forgot-password-footer-primary ${getButtonClass('btn-primary')}`}
              onClick={handleSendOtp}
              disabled={busy}
            >
              {busy ? '…' : t('login.forgotSendOtp')}
            </button>
          ) : null}
          {step === 2 ? (
            <button
              type="button"
              className={`btn main-popup-right-btn forgot-password-footer-primary ${getButtonClass('btn-primary')}`}
              onClick={handleCheckOtp}
              disabled={busy}
            >
              {busy ? '…' : t('login.forgotVerifyOtp')}
            </button>
          ) : null}
          {step === 3 ? (
            <button
              type="button"
              className={`btn main-popup-right-btn forgot-password-footer-primary ${getButtonClass('btn-primary')}`}
              onClick={handleChangePassword}
              disabled={busy}
            >
              {busy ? '…' : t('login.forgotSubmit')}
            </button>
          ) : null}
        </div>
      </div>
    </PopupWrapper>
  );
};

export default ForgotPasswordPopup;
